import { CalendarToday } from "@mui/icons-material"
import { Box } from "@mui/material"
import { useMemo, useState } from "react"
import intl from "src/internationalization/intl"
import {
    Button,
    CircularProgress,
    CommitmentAccordion,
    Grid,
    InnerSection,
    RankingCard,
    Section,
    Select,
    Stack,
    TextInput,
    Typography,
} from "src/lib"
import { formatCurrencyBRL } from "src/lib/transformers/ClientsOverviewInfo"
import { PriorityListIcon } from "src/lib/icons"
import { AdvisorRankingsCollapsible } from "./AdvisorRankingsCollapsible"
import { useCommitmentsSection } from "./useCommitmentsSection"
import { useRankingAssets } from "./useRankingAssets"
import type { ICommitmentsSectionProps } from "./types"

/** Cartão compacto do resumo executivo. */
const SummaryStat = ({
    value,
    label,
    accent = false,
}: {
    value: React.ReactNode
    label: string
    accent?: boolean
}) => (
    <Box
        sx={{
            flex: "1 1 140px",
            minWidth: 140,
            p: 1.5,
            borderRadius: 1,
            border: "1px solid rgba(255, 255, 255, 0.06)",
            bgcolor: "neutral.darkest",
        }}
    >
        <Typography variant="h4" sx={{ fontWeight: 700, color: accent ? "secondary.main" : undefined }}>
            {value}
        </Typography>
        <Typography variant="caption" color="quaternary">
            {label}
        </Typography>
    </Box>
)

/** Filtro em dropdown para a toolbar (converte os antigos TabFilters). */
const ToolbarSelect = ({
    label,
    filters,
    selected,
    onChange,
}: {
    label: string
    filters: any[]
    selected: any
    onChange: (filter: any) => void
}) => (
    <Box>
        <Typography variant="caption" color="quaternary" sx={{ display: "block", mb: 0.25 }}>
            {label}
        </Typography>
        <Select
            value={selected?.value ?? ""}
            onChange={(e: any) => {
                const found = filters.find((f) => f.value === e.target.value)
                if (found) onChange(found)
            }}
            options={filters.map((f) => ({ value: f.value, label: f.label }))}
            size="small"
        />
    </Box>
)

const CommitmentsSection = ({
    hideRanking = false,
    rankingOnly = false,
    rankingsReadOnly = false,
}: ICommitmentsSectionProps = {}) => {
    const {
        loading,
        error,
        finalCommitmentsList,
        isOpen,
        toggleAccordion,
        handleClickAIButton,
        totalEvents,
        resolvedLiquidityEventsCount,
        filteredClientOpportunities,
        liquidityFilters,
        selectedLiquidityFilter,
        handleLiquidityFilterChange,
        liquidityStreamFilters,
        selectedLiquidityStreamFilter,
        handleLiquidityStreamFilterChange,
        eventValueFilters,
        selectedEventValueFilter,
        handleEventValueFilterChange,
        periodFilters,
        selectedPeriodFilter,
        handlePeriodFilterChange,
        isEventOpen,
        toggleEventAccordion,
        expandAllEvents,
        collapseAllEvents,
    } = useCommitmentsSection()

    const { rankingAssets } = useRankingAssets()
    const [search, setSearch] = useState("")

    const toDateKey = (value?: string) => {
        if (!value) return ""
        const base = String(value).split("T")[0]
        const parts = base.split("-")
        if (parts.length !== 3) return base
        const [year, month, day] = parts
        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`
    }
    const todayKey = toDateKey(new Date().toISOString())

    // Busca por cliente ou produto a vencer.
    const visibleCommitments = useMemo(() => {
        const query = search.trim().toLowerCase()
        if (!query) return finalCommitmentsList
        return finalCommitmentsList
            .map((commitment: any) => ({
                ...commitment,
                clients: (commitment.clients ?? []).filter(
                    (cl: any) =>
                        (cl?.client?.name ?? "").toLowerCase().includes(query) ||
                        (cl?.produto_a_vencer?.nameProduct ?? "").toLowerCase().includes(query),
                ),
            }))
            .filter((commitment: any) => (commitment.clients?.length ?? 0) > 0)
    }, [finalCommitmentsList, search])

    // Resumo executivo
    const pendentes = Math.max(0, (totalEvents ?? 0) - (resolvedLiquidityEventsCount ?? 0))
    const totalImpact = useMemo(
        () =>
            finalCommitmentsList
                .flatMap((c: any) => c.clients ?? [])
                .reduce((sum: number, cl: any) => sum + (cl?.net_amount ?? 0), 0),
        [finalCommitmentsList],
    )

    if (rankingOnly) {
        return (
            <Section
                title={intl("rankingSectionTitle")}
                subtitle={intl("rankingSectionSubtitle")}
                icon={<PriorityListIcon />}
                onClickAIButton={handleClickAIButton}
            >
                {loading ? (
                    <Grid container justifyContent="center" sx={{ mt: 4, minHeight: "400px" }} alignItems="center">
                        <CircularProgress color="primary" />
                    </Grid>
                ) : error ? (
                    <Typography sx={{ mt: 2, color: (theme) => (theme as any).palette?.accentError?.main }}>
                        {error}
                    </Typography>
                ) : (
                    <Grid container gap={2} justifyContent="flex-start" sx={{ mt: 2 }}>
                        {rankingAssets.map((item, index) => (
                            <Grid item key={item.assetKey ?? index} sx={{ display: "flex" }}>
                                <RankingCard
                                    assetKey={item.assetKey}
                                    asset={item.asset}
                                    date={item.date}
                                    description={item.description}
                                    rankings={item.rankings}
                                    locked={item.locked}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Section>
        )
    }

    return (
        <Section title={intl("commitments")} icon={<CalendarToday />} onClickAIButton={handleClickAIButton}>
            {loading ? (
                <Grid container justifyContent="center" sx={{ mt: 4, minHeight: "400px" }} alignItems="center">
                    <CircularProgress color="primary" />
                </Grid>
            ) : error ? (
                <Typography sx={{ mt: 2, color: (theme) => (theme as any).palette?.accentError?.main }}>
                    {error}
                </Typography>
            ) : (
                <>
                    {/* Resumo executivo */}
                    <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                        <SummaryStat value={totalEvents ?? 0} label={intl("metricEvents")} />
                        <SummaryStat value={pendentes} label={intl("metricPending")} />
                        <SummaryStat value={resolvedLiquidityEventsCount ?? 0} label={intl("metricResolved")} />
                        <SummaryStat value={formatCurrencyBRL(totalImpact)} label={intl("metricImpacted")} />
                    </Box>

                    {/* Critérios de priorização — logo acima dos filtros/lista */}
                    {!hideRanking && rankingsReadOnly ? (
                        <Box sx={{ mt: 2 }}>
                            <AdvisorRankingsCollapsible />
                        </Box>
                    ) : null}
                    {!hideRanking && !rankingsReadOnly ? (
                        <Box sx={{ mt: 2 }}>
                            <InnerSection title={intl("rankingSectionTitle")}>
                                <Grid container gap={2} justifyContent="flex-start" sx={{ mt: 2 }}>
                                    {rankingAssets.map((item, index) => (
                                        <Grid item key={item.assetKey ?? index} sx={{ display: "flex" }}>
                                            <RankingCard
                                                assetKey={item.assetKey}
                                                asset={item.asset}
                                                date={item.date}
                                                description={item.description}
                                                rankings={item.rankings}
                                                locked={item.locked}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </InnerSection>
                        </Box>
                    ) : null}

                    {/* Toolbar única: busca + filtros */}
                    <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "flex-end" }}>
                        <Box sx={{ flex: "1 1 220px", minWidth: 200 }}>
                            <Typography variant="caption" color="quaternary" sx={{ display: "block", mb: 0.25 }}>
                                {intl("search")}
                            </Typography>
                            <TextInput
                                placeholder={intl("search")}
                                value={search}
                                onChange={(e: any) => setSearch(e.target.value)}
                            />
                        </Box>
                        <ToolbarSelect
                            label={intl("filterType")}
                            filters={liquidityStreamFilters}
                            selected={selectedLiquidityStreamFilter}
                            onChange={handleLiquidityStreamFilterChange}
                        />
                        <ToolbarSelect
                            label={intl("filterEventValue")}
                            filters={eventValueFilters}
                            selected={selectedEventValueFilter}
                            onChange={handleEventValueFilterChange}
                        />
                        <ToolbarSelect
                            label={intl("filterStatus")}
                            filters={liquidityFilters}
                            selected={selectedLiquidityFilter}
                            onChange={handleLiquidityFilterChange}
                        />
                        <ToolbarSelect
                            label={intl("filterPeriod")}
                            filters={periodFilters}
                            selected={selectedPeriodFilter}
                            onChange={handlePeriodFilterChange}
                        />
                    </Box>

                    {/* Expandir / recolher */}
                    <Grid container justifyContent="flex-end" gap={1} sx={{ mt: 2 }}>
                        <Button variant="outlined" color="neutral" onClick={collapseAllEvents}>
                            <Typography variant="body2">{intl("hideAll")}</Typography>
                        </Button>
                        <Button variant="outlined" color="neutral" onClick={expandAllEvents}>
                            <Typography variant="body2">{intl("showAll")}</Typography>
                        </Button>
                    </Grid>
                </>
            )}

            {!loading && !error && (
                <Stack spacing={2} sx={{ mt: 2 }}>
                    {visibleCommitments.map((commitment: any, idx: number) => {
                        const eventAccordionId = commitment.due_date + 1
                        const eventAccordionIsOpen = isEventOpen(eventAccordionId)
                        const isToday = toDateKey(commitment.due_date) === todayKey
                        return (
                            <CommitmentAccordion.Root
                                key={commitment.due_date}
                                commitmentId={eventAccordionId}
                                sx={{ mt: idx === 0 ? 0 : 1 }}
                            >
                                <CommitmentAccordion.Summary
                                    commitment={commitment}
                                    opportunitiesClientsCount={isToday ? filteredClientOpportunities.length : 0}
                                    open={eventAccordionIsOpen}
                                    onToggle={() => toggleEventAccordion(eventAccordionId)}
                                />
                                <CommitmentAccordion.Details
                                    commitment={commitment}
                                    open={eventAccordionIsOpen}
                                    isOpen={isOpen}
                                    toggleAccordion={toggleAccordion}
                                    clientOpportunities={isToday ? filteredClientOpportunities : []}
                                />
                            </CommitmentAccordion.Root>
                        )
                    })}
                </Stack>
            )}
        </Section>
    )
}

export { CommitmentsSection }
